// IMPL_07 (2026-07-20): mechanic spare-QR scan -> ₹30 coupon credit.
// Self-contained card for the mechanic dashboard (WebView). Camera scan via the
// native BarcodeDetector API where the WebView supports it (Chrome-based Android
// does); manual code entry works everywhere as the guaranteed fallback.
// Backend: POST /api/spares/unit/:code/mechanic-scan (mechanic JWT; ONE credit
// per unit ever, server-enforced) + GET /api/spares/credits/mine.
import React from 'react';
import axios from 'axios';

const RED = '#f42222';

export default class SpareScanCard extends React.Component {
  state = {
    open: false,
    scanning: false,
    manualCode: '',
    busy: false,
    result: null,      // { kind: 'ok'|'already'|'error', message, amount }
    totals: null,      // { total_unsettled, count }
    cameraError: null,
  };
  videoRef = React.createRef();
  stream = null;
  scanTimer = null;

  componentWillUnmount() { this.stopCamera(); }

  authHeaders = () => {
    const token = (this.props.getToken && this.props.getToken()) || null;
    return token ? { Authorization: 'Bearer ' + token } : {};
  };

  refreshTotals = async () => {
    try {
      const r = await axios.get('/api/spares/credits/mine', { headers: this.authHeaders() });
      if (r.data && r.data.ok) {
        this.setState({ totals: { total_unsettled: r.data.total_unsettled, count: (r.data.credits || []).length } });
      }
    } catch (_) { /* non-blocking */ }
  };

  // Accepts a raw unit code or a QR payload URL like https://ridenrepair.com/u/<code>
  extractCode = (text) => {
    const t = String(text || '').trim();
    const m = t.match(/\/u\/([A-Za-z0-9]+)/);
    return (m ? m[1] : t).toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  submitCode = async (raw) => {
    const code = this.extractCode(raw);
    if (!code || code.length < 8) {
      this.setState({ result: { kind: 'error', message: 'Invalid code. Scan the QR or type the full code.' } });
      return;
    }
    this.setState({ busy: true, result: null });
    try {
      const r = await axios.post(`/api/spares/unit/${code}/mechanic-scan`, {}, { headers: this.authHeaders() });
      const d = r.data || {};
      if (d.credited) {
        this.setState({ result: { kind: 'ok', message: d.message, amount: d.amount } });
      } else if (d.already) {
        this.setState({ result: { kind: 'already', message: d.message } });
      } else {
        this.setState({ result: { kind: 'error', message: d.message || 'Could not credit this scan.' } });
      }
      this.refreshTotals();
    } catch (err) {
      const msg = (err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
                  'Network error — try again.';
      this.setState({ result: { kind: 'error', message: String(msg) } });
    } finally {
      this.setState({ busy: false, scanning: false });
      this.stopCamera();
    }
  };

  startCamera = async () => {
    this.setState({ scanning: true, cameraError: null, result: null });
    try {
      if (!('BarcodeDetector' in window)) throw new Error('no_detector');
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const video = this.videoRef.current;
      video.srcObject = this.stream;
      await video.play();
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      this.scanTimer = setInterval(async () => {
        try {
          const codes = await detector.detect(video);
          if (codes && codes.length) {
            clearInterval(this.scanTimer);
            this.submitCode(codes[0].rawValue);
          }
        } catch (_) { /* per-frame errors are fine */ }
      }, 350);
    } catch (e) {
      this.stopCamera();
      this.setState({
        scanning: false,
        cameraError: 'Camera scan not available here — type the code printed under the QR instead.',
      });
    }
  };

  stopCamera = () => {
    if (this.scanTimer) { clearInterval(this.scanTimer); this.scanTimer = null; }
    if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }
  };

  render() {
    const { open, scanning, manualCode, busy, result, totals, cameraError } = this.state;
    return (
      <div style={{ margin: '8px 12px', border: '1px solid #222', borderRadius: 10, overflow: 'hidden',
                    background: '#111', color: '#fff' }}>
        <button
          onClick={() => { this.setState({ open: !open }); if (!open) this.refreshTotals(); }}
          style={{ width: '100%', background: '#111', color: '#fff', border: 'none', padding: '10px 14px',
                   display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          <span>🔩 Scan Spare QR — earn ₹30</span>
          <span style={{ color: RED }}>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div style={{ padding: '10px 14px', borderTop: `2px solid ${RED}` }}>
            {totals && (
              <div style={{ fontSize: 13, marginBottom: 8, color: '#bbb' }}>
                Unsettled credits: <b style={{ color: '#fff' }}>₹{totals.total_unsettled}</b> ({totals.count} scans)
              </div>
            )}
            {scanning ? (
              <div>
                <video ref={this.videoRef} playsInline muted
                       style={{ width: '100%', borderRadius: 8, border: `1px solid ${RED}` }} />
                <button onClick={() => { this.stopCamera(); this.setState({ scanning: false }); }}
                        style={{ marginTop: 8, background: '#333', color: '#fff', border: 'none',
                                 borderRadius: 8, padding: '8px 14px' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={this.startCamera} disabled={busy}
                        style={{ background: RED, color: '#fff', border: 'none', borderRadius: 8,
                                 padding: '10px 16px', fontWeight: 700 }}>
                  📷 Scan QR
                </button>
                <input value={manualCode}
                       onChange={(e) => this.setState({ manualCode: e.target.value })}
                       placeholder="or type the code"
                       style={{ flex: 1, minWidth: 140, background: '#000', color: '#fff',
                                border: '1px solid #444', borderRadius: 8, padding: '8px 10px' }} />
                <button onClick={() => this.submitCode(manualCode)} disabled={busy || !manualCode}
                        style={{ background: '#000', color: '#fff', border: `1px solid ${RED}`,
                                 borderRadius: 8, padding: '8px 14px' }}>
                  {busy ? '…' : 'Submit'}
                </button>
              </div>
            )}
            {cameraError && <div style={{ color: '#ffb020', fontSize: 13, marginTop: 8 }}>{cameraError}</div>}
            {result && (
              <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8,
                            background: result.kind === 'ok' ? '#0f2a12' : result.kind === 'already' ? '#26210c' : '#2a0f0f',
                            border: `1px solid ${result.kind === 'ok' ? '#2e7d32' : result.kind === 'already' ? '#b8860b' : RED}` }}>
                {result.kind === 'ok' ? `✅ ₹${result.amount} credited! ` : result.kind === 'already' ? '⚠️ ' : '❌ '}
                {result.message}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
